import * as React from 'react';
import {
  TagContainer,
  Tag as StyledTag,
  TagItemProps,
  AddTagInput,
  MoreTags,
  PopoverContainer,
  PopoverHeader,
  PopoverTitle,
  PopoverCloseAction,
  PopOverTags,
  TagPopoverWrapper,
} from '~/ui/components/Tags/Tags.styled';
import { CloseAlt } from '~/ui/assets/icons';
import { Popover } from 'antd';

import { TagOptions } from '~/ui/components/Tags/TagOptions';
import { PropsWithChildren, useEffect, useState } from 'react';
import { ColorsTypes } from '~/ui/theme/colors';
import { normalizeTagName } from '~/common/utils/formatTagName';

export interface TagItem extends Omit<TagItemProps, 'small' | 'rounded'> {
  id: string;
  tagId?: string;
  name: string;
  creationDate?: string;
}

export type TagPopoverProps = {
  options?: TagItem[];
  tags: TagItem[];
  closable?: boolean;
  onClose?: (tag: TagItem) => void;
  onAdd?: (tag: TagItem) => void;
  onCreate?: (newTag: string) => void;
  onRemove?: (tag: TagItem) => void;
  small?: boolean;
  rounded?: boolean;
  editable?: boolean;
  moreTagsBgColor?: string;
  moreTagsTextColor?: string;
  children: React.ReactNode;
};

export function TagPopover({
  options,
  tags,
  small = false,
  closable,
  onClose,
  onAdd,
  rounded,
  editable,
  onCreate,
  children,
  onRemove,
}: TagPopoverProps) {
  const [currentTags, setCurrentTags] = useState<TagItem[]>([]);
  const [isMoreTagsPopoverOpen, setIsMoreTagsPopoverOpen] = useState(false);
  const [isMoreTagsAddOpen, setIsMoreTagsAddOpen] = useState(false);

  const canShowAddTag = editable && options?.length;

  const handleAddTag = (tag: TagItem) => {
    setIsMoreTagsAddOpen(false);
    onAdd?.(tag);
  };

  const handleCreateTag = (tagName: string) => {
    setIsMoreTagsAddOpen(false);
    onCreate?.(tagName);
  };

  const sortedTags = tags
    .map((tag) => ({
      ...tag,
      name: normalizeTagName(tag.name),
    }))
    .sort((a, b) => new Date(a?.creationDate).getTime() - new Date(b?.creationDate).getTime());

  useEffect(() => {
    setCurrentTags(tags);
  }, [tags]);

  const renderTag = (tag: TagItem) => {
    const { colorPalette, textColor, bgColor } = tag;
    return (
      <StyledTag
        key={tag.id}
        colorPalette={colorPalette}
        textColor={textColor}
        bgColor={bgColor}
        small
        closable={closable}
        rounded
        onClose={() => onClose?.(tag)}
      >
        {tag.name}
        {closable ? <CloseAlt className="action-icon" width={9} height={9} onClick={onRemove?.(tag)} /> : null}
      </StyledTag>
    );
  };

  const renderAddTagButton = () => (
    <AddTagInput small={small} onClick={() => setIsMoreTagsAddOpen(true)} className="add-tag">
      + Tag
    </AddTagInput>
  );

  const renderMoreTagsPopoverContent = () => (
    <PopoverContainer>
      <PopoverHeader>
        <div>
          <PopoverTitle>Tags da person</PopoverTitle>
        </div>
        <PopoverCloseAction
          onClick={() => {
            setIsMoreTagsAddOpen(false);
            setIsMoreTagsPopoverOpen(false);
          }}
        >
          <CloseAlt className="action-icon" width={13} height={13} />
        </PopoverCloseAction>
      </PopoverHeader>

      <Popover
        trigger="click"
        placement="bottomLeft"
        overlayClassName="mt-popover-no-arrow mt-popover-no-padding"
        visible={isMoreTagsAddOpen}
        content={
          <TagOptions currentTags={currentTags} options={options} onAdd={handleAddTag} onCreate={handleCreateTag} />
        }
      >
        <PopOverTags>
          {sortedTags.map(renderTag)}
          {canShowAddTag && renderAddTagButton()}
        </PopOverTags>
      </Popover>
    </PopoverContainer>
  );

  return (
    <Popover
      trigger="click"
      placement="bottomLeft"
      overlayClassName="mt-popover-no-arrow mt-popover-no-padding mt-popover-border-radius"
      visible={isMoreTagsPopoverOpen}
      onVisibleChange={(visible) => {
        if (!visible) {
          setIsMoreTagsAddOpen(visible);
        }

        setIsMoreTagsPopoverOpen(visible);
      }}
      content={renderMoreTagsPopoverContent()}
    >
      <TagPopoverWrapper>{children}</TagPopoverWrapper>
    </Popover>
  );
}
